import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import { FaRulerHorizontal, FaRulerVertical, FaTruck } from "react-icons/fa";
import { GoHeart, GoHeartFill } from "react-icons/go";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import FurnitureCard from "../FurnitureCard.js";
import PageHeader from "../PageHeader.js";
import { FurnitureSearch } from "../../App.js";
import defaultImg from "../../images/defaultImg.webp";
import { useTranslation } from "react-i18next";
import { RiVerifiedBadgeFill, RiEmotionSadLine } from "react-icons/ri";
import DepthIcon from "../../images/depthicon.png";
import "../pages/util/custom-slick.css";
import AlwaysTop from "./util/AlwaysTop.js";
import { postReq, getReq } from "../../configs/ApiUrl.js";
import PageFooter from "../PageFooter.js";
import { motion } from "framer-motion";
import StraightenIcon from "@mui/icons-material/Straighten";
import HeightIcon from "@mui/icons-material/Height";
import WidthFullIcon from "@mui/icons-material/WidthFull";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import ChecklistIcon from "@mui/icons-material/Checklist";
import Button from "../Button.js";
import Label from "../ConditionLabel.js";
import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";

function CatalogueProduct() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [extraGrandTotal, setExtraGrandTotal] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [favourite, setFavourite] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation(["productPage", "altText", "utilityPages"]);
  const [searchTerm, setSearchTerm] = useContext(FurnitureSearch);

  const [display, setDisplay] = useState({
    mainDisplay: searchTerm.productDisplayMain,
    zoomDisplay: searchTerm.productDisplayZoom,
  });

  const [addProduct, setAddProduct] = useState({
    plusMinusDisplay: 0,
    discountDisplay: 1,
    totalDisplay: 0,
    productDisplay: "",
  });

  const [addMoving, setAddMoving] = useState({
    movingDisplay: 0,
    Discount: 1,
  });

  const [extra, setExtra] = useState({
    activeObject: null,
    extraArray: [],
    numbers: [],
    plusButton: [],
    extraTotal: [],
    allAddOnsSelected: false,
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await getReq(`/product/${productId}`);
        if (response?.product) {
          setProduct(response.product);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (product && !initialized) {
      setAddProduct({
        plusMinusDisplay: 1,
        discountDisplay: 1,
        totalDisplay: parseFloat(product.price),
        productDisplay:
          product.price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
          " kr",
      });

      setAddMoving({
        movingDisplay: parseFloat(product.deliveryPrice),
        Discount: 1,
      });

      setExtra((prev) => ({
        ...prev,
        extraArray: product.addOns,
        numbers: Array(product.addOns?.length || 0).fill(0),
        plusButton: Array(product.addOns?.length || 0).fill("visible"),
        extraTotal: Array(product.addOns?.length || 0).fill(0),
      }));

      setInitialized(true);
    }
  }, [product, initialized]);

  useEffect(() => {
    if (product) {
      // Calculate original total without applying any discount
      const originalTotal =
        addProduct.totalDisplay + addMoving.movingDisplay + extraGrandTotal;

      // Apply bonus discount if applicable to calculate the final total
      let calculatedTotal = originalTotal;

      if (extra.allAddOnsSelected && product.bonusDiscount) {
        calculatedTotal -= product.bonusDiscount;
      }

      // Set both totals: final total (with discount) and original total (without discount)
      setFinalTotal({ calculatedTotal, originalTotal });
    }
  }, [
    addProduct.totalDisplay,
    addMoving.movingDisplay,
    extraGrandTotal,
    extra.allAddOnsSelected,
    product,
  ]);

  const NextArrow = (props) => (
    <div
      className='absolute top-1/2 right-2 transform -translate-y-1/2 z-10 cursor-pointer bg-gray-400 opacity-75 text-white p-2 rounded-full shadow-md hover:bg-gray-800 transition'
      onClick={props.onClick}>
      <MdChevronRight size={24} />
    </div>
  );

  const PrevArrow = (props) => (
    <div
      className='absolute top-1/2 left-2 transform -translate-y-1/2 z-10 cursor-pointer bg-gray-400 opacity-75 text-white p-2 rounded-full shadow-md hover:bg-gray-800 transition'
      onClick={props.onClick}>
      <MdChevronLeft size={24} />
    </div>
  );

  const imageSet = Array.from(
    new Set(
      [product?.mainImage, ...(product?.imageNames || [])].filter(Boolean)
    )
  );

  const settings = {
    dots: false,
    arrows: imageSet.length > 1,
    infinite: imageSet.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const handleAddToCart = async () => {
    if (addProduct.plusMinusDisplay === 0) return;

    const calculatedTotal =
      addProduct.totalDisplay + addMoving.movingDisplay + extraGrandTotal;
    const finalTotal =
      extra.allAddOnsSelected && product.bonusDiscount
        ? calculatedTotal - product.bonusDiscount
        : calculatedTotal;

    const orderObject = {
      productId: product._id,
      quantity: addProduct.plusMinusDisplay,
      discount: product.discount || 0,
      bonusDiscount: extra.allAddOnsSelected ? product.bonusDiscount || 0 : 0,
      productCost: addProduct.totalDisplay,
      movingCost: addMoving.movingDisplay || 0,
      extras: extra.extraArray.map((item, i) => ({
        item: item.item,
        quantity: extra.numbers[i],
        cost: extra.extraTotal[i],
      })),
      grandTotal: finalTotal,
      image: product.mainImage || product.imageNames[0],
    };

    // Send the updated cart to the backend
    try {
      const response = await postReq("/user/update-cart", {
        userId: localStorage.getItem("userId"),
        cartItems: [orderObject],
      });

      if (!response.success) {
        console.error("Failed to update cart on the server:", response.message);
      } else {
      }
    } catch (error) {
      console.error("Error updating cart on the server:", error);
    }

    // Navigate to checkout
    navigate("/checkout");
  };

  const resetSelections = () => {
    setAddProduct({
      plusMinusDisplay: 0,
      discountDisplay: 1,
      totalDisplay: 0,
      productDisplay: "",
    });
    setAddMoving({ movingDisplay: 0, Discount: 1 });
    setExtra({
      ...extra,
      numbers: Array(extra.extraArray?.length || 0).fill(0),
      plusButton: Array(extra.extraArray?.length || 0).fill("visible"),
      extraTotal: Array(extra.extraArray?.length || 0).fill(0),
      allAddOnsSelected: false,
    });
    setExtraGrandTotal(0);
  };

  useEffect(() => {
    AlwaysTop();
  }, []);

  if (loading) {
    return null;
  }

  if (product?.mainImage && !imageSet.includes(product.mainImage)) {
    imageSet.unshift(product.mainImage);
  }

  if (!product) {
    return (
      <div className='flex flex-grow flex-col justify-center items-center p-4'>
        <RiEmotionSadLine
          size={100}
          className='mb-2 text-stone-500'
        />
        <h2 className='mb-8 text-center font-semibold text-stone-500'>
          {t("productPage:noMatch")}
        </h2>
        <Link
          className='w-full'
          to='/shopping-home'>
          <button className='btn-main w-full'>
            {t("utilityPages:returnHomeButton")}
          </button>
        </Link>
      </div>
    );
  }

  return (
    <Box
      display='flex'
      flexDirection='column'
      minHeight='100vh'
      bgcolor='white'>
      <Box mb={5}>
        <PageHeader />
      </Box>

      {!loading && product ? (
        <Container maxWidth='lg'>
          <Box
            display='flex'
            alignItems='center'
            py={6}
            onClick={() => {
              resetSelections();
              navigate("/shopping-home");
            }}
            sx={{ cursor: "pointer" }}>
            <img
              src='./../assets/icons/arrow.svg'
              alt={t("altText:backArrow")}
              width={45}
              className='back-btn drop-shadow'
            />
          </Box>

          <Grid
            container
            spacing={4}>
            <Grid
              item
              xs={12}
              md={6}>
              <Paper
                elevation={2}
                sx={{ overflow: "hidden", height: 300 }}>
                <motion.div
                  layout
                  style={{ height: "100%" }}>
                  <Slider {...settings}>
                    {imageSet.map((img, i) => (
                      <Box
                        key={i}
                        sx={{
                          height: 300,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                        <Box
                          component='img'
                          src={img || defaultImg}
                          alt={product.title}
                          onError={(e) => (e.target.src = defaultImg)}
                          sx={{
                            height: "100%", // this is the key line for vertical centering
                            objectFit: "contain",
                            mx: "auto",
                            display: "block",
                          }}
                        />
                      </Box>
                    ))}
                  </Slider>
                </motion.div>
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <Stack spacing={2}>
                <Typography
                  variant='h4'
                  fontWeight='bold'>
                  {product.title}
                </Typography>
                <Box
                  display='flex'
                  alignItems='center'
                  gap={2}>
                  <Label
                    text={
                      product.condition?.toLowerCase() === "new"
                        ? "New"
                        : "Used"
                    }
                    approved={false}
                    className='static ml-0 w-auto rounded px-2 py-1'
                  />
                  {product.isQualityVerified && (
                    <Box
                      display='flex'
                      alignItems='center'
                      color='green'>
                      <RiVerifiedBadgeFill style={{ marginRight: 4 }} />
                      <Typography variant='body2'>
                        {t("productPage:qualityVerified")}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  variant='h4'
                  fontWeight='bold'>
                  {product.price
                    .toFixed(0)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
                  kr
                </Typography>
                <Box
                  display='flex'
                  alignItems='center'
                  color='text.secondary'>
                  <FaTruck style={{ marginRight: 8 }} />
                  <Typography variant='body2'>
                    {product.deliveryPrice
                      ? `${product.deliveryPrice} kr`
                      : t("productPage:freeDelivery")}
                  </Typography>
                </Box>
                <Box
                  display='flex'
                  alignItems='center'
                  gap={2}>
                  <IconButton
                    onClick={() => {
                      if (addProduct.plusMinusDisplay > 1) {
                        setAddProduct((prev) => ({
                          ...prev,
                          plusMinusDisplay: prev.plusMinusDisplay - 1,
                          totalDisplay:
                            (prev.plusMinusDisplay - 1) * product.price,
                        }));
                      }
                    }}>
                    −
                  </IconButton>
                  <Typography variant='h6'>
                    {addProduct.plusMinusDisplay}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      if (addProduct.plusMinusDisplay < product.quantity) {
                        setAddProduct((prev) => ({
                          ...prev,
                          plusMinusDisplay: prev.plusMinusDisplay + 1,
                          totalDisplay:
                            (prev.plusMinusDisplay + 1) * product.price,
                        }));
                      }
                    }}>
                    +
                  </IconButton>
                </Box>
                <Button
                  variant='main'
                  className='w-full'
                  onClick={handleAddToCart}>
                  {t("productPage:addButton")}
                </Button>
              </Stack>
            </Grid>

            {product.addOns?.length > 0 && (
              <Grid
                item
                xs={12}
                sx={{ mt: 2 }}>
                <Paper
                  elevation={1}
                  sx={{ p: 3 }}>
                  <Typography
                    variant='h6'
                    gutterBottom>
                    {t("productPage:extrasMessage")}
                  </Typography>
                  {product.addOns.map((item, i) => (
                    <Box
                      key={i}
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                      borderBottom={1}
                      borderColor='divider'
                      py={2}>
                      <Typography sx={{ flex: 1 }}>{item.item}</Typography>

                      <Box
                        display='flex'
                        alignItems='center'
                        gap={1}>
                        <IconButton
                          size='small'
                          disabled={extra.numbers[i] === 0}
                          onClick={() => {
                            if (extra.numbers[i] === 0) return;
                            const updated = [...extra.numbers];
                            updated[i]--;
                            const newTotals = [...extra.extraTotal];
                            newTotals[i] = item.price * updated[i];
                            const newGrand = newTotals.reduce(
                              (a, b) => a + b,
                              0
                            );
                            setExtra((prev) => ({
                              ...prev,
                              numbers: updated,
                              extraTotal: newTotals,
                              allAddOnsSelected: updated.every(
                                (num) => num > 0
                              ),
                            }));
                            setExtraGrandTotal(newGrand);
                          }}>
                          −
                        </IconButton>
                        <Typography>{extra.numbers[i]}</Typography>
                        <IconButton
                          size='small'
                          disabled={extra.numbers[i] === item.number}
                          onClick={() => {
                            if (extra.numbers[i] === item.number) return;
                            const updated = [...extra.numbers];
                            updated[i]++;
                            const newTotals = [...extra.extraTotal];
                            newTotals[i] = item.price * updated[i];
                            const newGrand = newTotals.reduce(
                              (a, b) => a + b,
                              0
                            );
                            setExtra((prev) => ({
                              ...prev,
                              numbers: updated,
                              extraTotal: newTotals,
                              allAddOnsSelected: updated.every(
                                (num) => num > 0
                              ),
                            }));
                            setExtraGrandTotal(newGrand);
                          }}>
                          +
                        </IconButton>
                      </Box>

                      <Typography
                        sx={{
                          minWidth: 80,
                          textAlign: "right",
                          fontWeight: "bold",
                        }}>
                        {extra.extraTotal[i]} kr
                      </Typography>
                    </Box>
                  ))}
                  <Box mt={2}>
                    {extra.allAddOnsSelected && product?.bonusDiscount > 0 ? (
                      <Typography
                        color='success.main'
                        fontWeight='bold'>
                        {t("productPage:bonusDiscountLabel")}{" "}
                        {product.bonusDiscount} kr –{" "}
                        {t("productPage:bonusDiscountApplied")}
                      </Typography>
                    ) : product?.bonusDiscount > 0 ? (
                      <Typography color='text.secondary'>
                        {t("productPage:bonusDiscountMessage")}{" "}
                        {product.bonusDiscount} kr
                      </Typography>
                    ) : (
                      <Typography color='text.disabled'>
                        {t("productPage:noBonusDiscount")}
                      </Typography>
                    )}
                  </Box>
                  <Box mt={2}>
                    <Typography
                      variant='body2'
                      color='success.main'>
                      {t("productPage:bonusDiscountMessage")}{" "}
                      {product.bonusDiscount} kr
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Additional Info Sections */}
            <Grid
              item
              xs={12}>
              <Paper
                elevation={1}
                sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  gutterBottom>
                  {t("productPage:descLabel")}
                </Typography>
                <Typography
                  variant='body1'
                  color='text.secondary'>
                  {product.description}
                </Typography>
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}>
              <Paper
                elevation={1}
                sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  gutterBottom>
                  {t("productPage:detailsLabel")}
                </Typography>
                <Stack spacing={1}>
                  <Box
                    display='flex'
                    justifyContent='space-between'>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}>
                      <ColorLensIcon fontSize='small' />
                      <Typography variant='body2'>
                        {t("productPage:color")}
                      </Typography>
                    </Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'>
                      {product.color}
                    </Typography>
                  </Box>
                  <Box
                    display='flex'
                    justifyContent='space-between'>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}>
                      <ChecklistIcon fontSize='small' />
                      <Typography variant='body2'>
                        {t("productPage:condition")}
                      </Typography>
                    </Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'>
                      {product.condition}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}>
              <Paper
                elevation={1}
                sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  gutterBottom>
                  {t("productPage:dimensionsLabel")}
                </Typography>
                <Stack spacing={1}>
                  <Box
                    display='flex'
                    justifyContent='space-between'>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}>
                      <HeightIcon fontSize='small' />
                      <Typography variant='body2'>
                        {t("productPage:height")}
                      </Typography>
                    </Box>
                    <Typography variant='body2'>
                      {product.dimension?.height} cm
                    </Typography>
                  </Box>
                  <Box
                    display='flex'
                    justifyContent='space-between'>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}>
                      <StraightenIcon fontSize='small' />
                      <Typography variant='body2'>
                        {t("productPage:length")}
                      </Typography>
                    </Box>
                    <Typography variant='body2'>
                      {product.dimension?.width} cm
                    </Typography>
                  </Box>
                  <Box
                    display='flex'
                    justifyContent='space-between'>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}>
                      <WidthFullIcon fontSize='small' />
                      <Typography variant='body2'>
                        {t("productPage:depth")}
                      </Typography>
                    </Box>
                    <Typography variant='body2'>
                      {product.dimension?.depth} cm
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      ) : (
        <Box
          display='flex'
          flexDirection='column'
          flexGrow={1}
          alignItems='center'
          justifyContent='center'
          p={4}>
          <RiEmotionSadLine
            size={100}
            className='mb-2'
            color='gray'
          />
          <Typography
            variant='h6'
            color='text.secondary'
            gutterBottom>
            {t("productPage:noMatch")}
          </Typography>
          <Link
            to='/shopping-home'
            style={{ width: "100%" }}>
            <button className='btn-main w-full'>
              {t("utilityPages:returnHomeButton")}
            </button>
          </Link>
        </Box>
      )}

      <Box
        sx={{ mb: 4 }}
        display={{ xs: "block", md: "none" }}>
        <PageFooter />
      </Box>
    </Box>
  );
}

export default CatalogueProduct;
